import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Nav, NavItem, NavLink, Spinner } from "reactstrap";

import { AUTOPILOT_COLORS } from "../../../components/autopilot/styles";
import { useAuth } from "../../../hooks/auth";
import api from "../../../services/api";
import { temPermissao } from "../AuditoriaPedido/regras";
import ContatosEmbarcador from "../Contatos";
import Origens from "../Origens";
import { TabDados, TabToken } from "./abas";

// Cadastro do embarcador em abas (sprint 638, épico PO1-2280): Dados gerais,
// Origens / CDs, Contatos e Token. A antiga aba "Filiais" saiu: lia a mesma
// tabela (EmbarcadorRemetente) que Origens / CDs, com menos colunas; o único dado
// dela que Origens não mostra, o token por origem, foi para a aba Token. É o mesmo componente na página do
// cliente (o cadastro DELE, pelo embarcador do topo) e no modal que a Everlog
// abre a partir da lista. Cada aba só aparece para quem tem a permissão dela:
// - Dados gerais: Pages.Cadastro.Embarcador (a permissão da tela antiga);
// - Origens / CDs: hub.operacao.pedidos.visualizar (a do menu antigo de Origens);
// - Contatos: hub.embarcadores.contatos (a do menu antigo de Contatos);
// - Token: só o admin Everlog (gerar e sincronizar já eram só dele no backend).
// O admin Everlog vê todas (bypass do temPermissao).

export const PERMISSAO_CADASTRO = "Pages.Cadastro.Embarcador";
export const PERMISSAO_ORIGENS = "hub.operacao.pedidos.visualizar";
export const PERMISSAO_CONTATOS = "hub.embarcadores.contatos";

export const ABAS = [
  { id: "dados", label: "Dados gerais" },
  { id: "origens", label: "Origens / CDs" },
  { id: "contatos", label: "Contatos" },
  { id: "token", label: "Token" },
];

export function abasPermitidas(user) {
  const cadastro = temPermissao(user, PERMISSAO_CADASTRO);
  const permitido = {
    dados: cadastro,
    origens: temPermissao(user, PERMISSAO_ORIGENS),
    contatos: temPermissao(user, PERMISSAO_CONTATOS),
    token: Boolean(user && user.setor === "admin"),
  };
  return ABAS.filter((a) => permitido[a.id]).map((a) => a.id);
}

const navStyle = {
  cursor: "pointer",
  fontSize: "0.82rem",
  padding: "0.55rem 0.9rem",
  color: AUTOPILOT_COLORS.muted,
  borderRadius: "4px 4px 0 0",
};
const navActiveStyle = {
  ...navStyle,
  color: AUTOPILOT_COLORS.title,
  borderBottom: "2px solid var(--theme-accent)",
  fontWeight: 600,
};
const mutedStyle = { color: AUTOPILOT_COLORS.muted, padding: "1rem 0" };

const EmbarcadorCadastro = ({ embarcadorId, embarcadorNome, aba, abaInicial, onAbaChange, onDados }) => {
  const { user } = useAuth();
  const permitidas = useMemo(() => abasPermitidas(user), [user]);
  const [abaInterna, setAbaInterna] = useState(abaInicial);
  // Link antigo com ?aba=filiais cai em Origens / CDs (mesmos registros).
  const pedidaBruta = aba !== undefined ? aba : abaInterna;
  const pedida = pedidaBruta === "filiais" ? "origens" : pedidaBruta;
  const abaAtiva = permitidas.includes(pedida) ? pedida : permitidas[0];

  const usaDetalhe = permitidas.includes("dados");
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);

  const carregar = useCallback(async () => {
    if (!embarcadorId || !usaDetalhe) return;
    try {
      setLoading(true);
      setErro(null);
      setDados(null);
      const res = await api.get(`/embarcadores/${embarcadorId}/detalhe`);
      setDados(res.data);
      if (onDados) onDados(res.data);
    } catch (e) {
      setErro("Erro ao carregar dados do embarcador.");
    } finally {
      setLoading(false);
    }
    // onDados é callback do pai; recarregar só quando o embarcador muda.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embarcadorId, usaDetalhe]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const escolher = (id) => {
    if (aba === undefined) setAbaInterna(id);
    if (onAbaChange) onAbaChange(id);
  };

  if (!permitidas.length) {
    return <div style={mutedStyle}>Seu perfil não tem acesso a nenhuma parte do cadastro do embarcador.</div>;
  }

  const nome = (dados && (dados.fantasia || dados.razaoSocial)) || embarcadorNome;
  const filiais = dados && dados.filiais;

  return (
    <div data-testid="embarcador-cadastro">
      <div style={{ borderBottom: `1px solid ${AUTOPILOT_COLORS.border}`, marginBottom: "1rem" }}>
        <Nav style={{ margin: 0 }}>
          {ABAS.filter((a) => permitidas.includes(a.id)).map((a) => (
            <NavItem key={a.id}>
              <NavLink style={abaAtiva === a.id ? navActiveStyle : navStyle} onClick={() => escolher(a.id)} data-aba={a.id}>
                {a.label}
              </NavLink>
            </NavItem>
          ))}
        </Nav>
      </div>

      {abaAtiva === "dados" && loading ? (
        <div style={{ textAlign: "center", padding: "3rem" }}>
          <Spinner style={{ color: AUTOPILOT_COLORS.accent }} />
        </div>
      ) : null}
      {abaAtiva === "dados" && !loading && erro ? (
        <div style={{ color: AUTOPILOT_COLORS.statusDanger, padding: "1rem 0" }}>{erro}</div>
      ) : null}
      {abaAtiva === "dados" && !loading && !erro ? <TabDados dados={dados} /> : null}
      {abaAtiva === "origens" ? <Origens embutido embarcadorId={embarcadorId} embarcadorNome={nome} /> : null}
      {abaAtiva === "contatos" ? <ContatosEmbarcador embutido embarcadorId={embarcadorId} embarcadorNome={nome} /> : null}
      {abaAtiva === "token" ? <TabToken embarcadorId={embarcadorId} filiais={filiais} /> : null}
    </div>
  );
};

export default EmbarcadorCadastro;
